import BugReportIcon from '@mui/icons-material/BugReport';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import RefreshIcon from '@mui/icons-material/Refresh';
import SpeedIcon from '@mui/icons-material/Speed';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  lighten,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { getWorkUnitsStatus, deleteWorkUnit } from 'API/linkedin-funnel';
import { getAllLinkedinAccounts } from 'API/linkedin-accounts';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale/de';
import { enUS } from 'date-fns/locale/en-US';
import PaiButton from 'features/ui/base-components/pai-button';
import { useUserData } from 'hooks/use-user-data';
import i18n from 'i18n';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { FunnelFunctions } from '../co-pilot.types';
import DeleteWorkUnitDialog from './delete-work-unit-dialog';

interface ProcessingWorkUnitProps {
  workUnit: WorkUnit;
  getWorkTypeColor: (workType: string) => string;
  getPriorityColor: (priority: string) => string;
  getFunctionName: (functionName: string) => string;
  onDelete: (workUnit: WorkUnit) => void;
  getLinkedinAccountDisplayName: (workUnit: WorkUnit) => string;
  getPersonDisplayName: (workUnit: WorkUnit) => string;
}

const ProcessingWorkUnit: React.FC<ProcessingWorkUnitProps> = ({
  workUnit,
  getWorkTypeColor,
  getPriorityColor,
  getFunctionName,
  onDelete,
  getLinkedinAccountDisplayName,
  getPersonDisplayName,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const getWorkTypeKey = (workType: string) => {
    return _.camelCase(`workUnit-${workType}`);
  };

  return (
    <Box className={classes.workItem}>
      {/* Status indicator and delete button in top right */}
      <Box className={classes.workItemStatus}>
        <Box className={classes.statusIndicator} />
        <Typography className={classes.statusText}>{t('working')}</Typography>
        <IconButton
          size="small"
          onClick={() => onDelete(workUnit)}
          className={classes.deleteButton}
          title={t('interrupt')}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Main content */}
      <Box>
        {/* Header with campaign name and work type */}
        <Stack direction="row" alignItems="center" gap={1} className={classes.workItemHeader}>
          <Chip
            label={t(getWorkTypeKey(workUnit.workType))}
            size="small"
            className={classes.workTypeChip}
            style={{ backgroundColor: getWorkTypeColor(workUnit.workType), color: 'white' }}
          />
          <Chip
            label={t(workUnit.priority)}
            size="small"
            variant="outlined"
            className={classes.priorityChip}
            style={{ borderColor: getPriorityColor(workUnit.priority), color: getPriorityColor(workUnit.priority) }}
          />
          {typeof workUnit.isWorking === 'boolean' && (
            <Chip
              label={workUnit.isWorking ? t('inWorktime') : t('outsideWorktime')}
              size="small"
              variant="outlined"
              className={classes.priorityChip}
              color={workUnit.isWorking ? 'success' : 'warning'}
            />
          )}
        </Stack>

        {/* Campaign name */}
        <Typography className={classes.workItemTitle}>{workUnit.funnel?.name || t('unknownCampaign')}</Typography>

        {/* Info grid - simplified */}
        <Box className={classes.workItemInfoGrid}>
          <Box className={classes.infoItem}>
            {workUnit.step && (
              <>
                <Typography className={classes.infoLabel}>{t('currentStep')}</Typography>
                <Typography className={classes.infoValue}>{getFunctionName(workUnit.step.functionName || '')}</Typography>
              </>
            )}
          </Box>

          <Box className={classes.infoItem}>
            <Typography className={classes.infoLabel}>{t('person')}</Typography>
            <Typography className={classes.infoValue}>{getPersonDisplayName(workUnit)}</Typography>
          </Box>

          <Box className={classes.infoItem}>
            <Typography className={classes.infoLabel}>{t('linkedinAccount')}</Typography>
            <Typography className={classes.infoValue}>{getLinkedinAccountDisplayName(workUnit)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

interface QueueWorkUnitProps {
  workUnit: WorkUnit;
  getWorkTypeColor: (workType: string) => string;
  // removed time estimate from UI by request
  getFunctionName: (functionName: string) => string;
  onDelete: (workUnit: WorkUnit) => void;
  getLinkedinAccountDisplayName: (workUnit: WorkUnit) => string;
  getPersonDisplayName: (workUnit: WorkUnit) => string;
}

const QueueWorkUnit: React.FC<QueueWorkUnitProps> = ({ workUnit,
  getWorkTypeColor, getFunctionName, onDelete, getLinkedinAccountDisplayName, getPersonDisplayName }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const getWorkTypeKey = (workType: string) => {
    return _.camelCase(`workUnit-${workType}`);
  };

  return (
    <Box key={workUnit.id} className={classes.queueItem}>
      {/* Status indicator and delete button in top right */}
      <Box className={classes.queueItemStatus}>
        <Box className={classes.queueStatusIndicator} />
        <Typography className={classes.queueStatusText}>{t('pending')}</Typography>
        <IconButton
          size="small"
          onClick={() => onDelete(workUnit)}
          className={classes.deleteButton}
          title={t('delete')}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Main content */}
      <Box>
        {/* Header with queue position and work type */}
        <Stack direction="row" alignItems="center" gap={1} className={classes.queueItemHeader}>
          <Box className={classes.queuePosition}>#{workUnit.queuePosition}</Box>
          <Chip
            label={t(getWorkTypeKey(workUnit.workType))}
            size="small"
            className={classes.queueWorkTypeChip}
            style={{ backgroundColor: getWorkTypeColor(workUnit.workType), color: 'white' }}
          />
          {typeof workUnit.isWorking === 'boolean' && (
            <Chip
              label={workUnit.isWorking ? t('inWorktime') : t('outsideWorktime')}
              size="small"
              variant="outlined"
              className={classes.queueWorkTypeChip}
              color={workUnit.isWorking ? 'success' : 'warning'}
            />
          )}
        </Stack>

        {/* Campaign name */}
        <Typography className={classes.queueItemTitle}>{workUnit.funnel?.name || t('unknownCampaign')}</Typography>

        {/* Info grid - simplified */}
        <Box className={classes.queueItemInfoGrid}>
          <Box className={classes.infoItem}>
            {workUnit.step && (
              <>
                <Typography className={classes.infoLabel}>{t('nextStep')}</Typography>
                <Typography className={classes.infoValue}>{getFunctionName(workUnit.step.functionName || '')}</Typography>
              </>
            )}
          </Box>

          <Box className={classes.infoItem}>
            <Typography className={classes.infoLabel}>{t('person')}</Typography>
            <Typography className={classes.infoValue}>{getPersonDisplayName(workUnit)}</Typography>
          </Box>

          <Box className={classes.infoItem}>
            <Typography className={classes.infoLabel}>{t('linkedinAccount')}</Typography>
            <Typography className={classes.infoValue}>{getLinkedinAccountDisplayName(workUnit)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

interface WorkUnit {
  id: string;
  workType: string;
  priority: 'high' | 'low';
  status: 'pending' | 'processing' | 'failed' | 'finished';
  createdAt: string;
  startedAt?: string;
  estimatedDuration: number;
  runningFor?: number;
  estimatedCompletion?: string;
  queuePosition?: number;
  estimatedStartTime?: string;
  isWorking?: boolean;
  funnel?: {
    id: string;
    name: string;
    status: string;
    currentStepIndex: number;
    messageWeight: number;
  };
  step?: {
    index: number;
    functionName: string;
    name: string;
  };
  account?: {
    email: string;
    messagesUsed: number;
    messagesLimit: number;
  };
  specs: Record<string, any>;
  contact?: {
    name?: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
  };
}

interface WorkUnitsStatus {
  timestamp: string;
  statistics: {
    totalPending: number;
    totalProcessing: number;
    recentFailures: number;
    maxConcurrent: number;
    systemLoad: number;
    worktimeBreakdown?: {
      inWorktime: number;
      outOfWorktime: number;
      unknown: number;
    };
  };
  processing: WorkUnit[];
  pending: WorkUnit[];
  nextRefreshRecommended: string;
}

interface Props {
  open: boolean;
  funnelId?: string;
  handleClose: () => void;
}

const WorkUnitsStatusModal: React.FC<Props> = ({ open, funnelId, handleClose }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { token } = useUserData();
  const theme = useTheme();

  const [status, setStatus] = useState<WorkUnitsStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    loading: boolean;
    workUnit: WorkUnit | null;
  }>({
    open: false,
    loading: false,
    workUnit: null,
  });
  const [accountNames, setAccountNames] = useState<Record<string, string>>({});

  const fetchWorkUnitsStatus = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getWorkUnitsStatus(funnelId);
      if (response.error) {
        setError(response.error.message || 'Failed to fetch work units status');
      } else {
        setStatus(response);
        // get linkedin account names for display - use data from getAllLinkedInAccounts instead of individual API calls
        try {
          const ids = Array.from(
            new Set(
              [...(response.processing || []), ...(response.pending || [])]
                .map((u: WorkUnit) => _.get(u, 'specs.accountId'))
                .filter((id: any): id is string => typeof id === 'string' && id.length > 0)
            )
          );
          const missing = ids.filter((id) => !accountNames[id]);
          if (missing.length > 0) {
            // 🚨 FIX: Get all LinkedIn accounts once instead of individual calls
            const allAccountsRes = await getAllLinkedinAccounts();
            if (!allAccountsRes.error && allAccountsRes.allLinkedInAccounts) {
              const results = missing.map((id) => {
                const account = allAccountsRes.allLinkedInAccounts.find((acc: any) => acc._id === id);
                if (account) {
                  const name = _.compact([account.firstName, account.lastName]).join(' ').trim();
                  const display = name || account.userEmail || id;
                  return { id, display };
                } else {
                  return { id, display: id };
                }
              });

              if (results.length > 0) {
                setAccountNames((prev) => {
                  const copy = { ...prev };
                  results.forEach((r) => (copy[r.id] = r.display));
                  return copy;
                });
              }
            }
          }
        } catch {
          // ignore account name fetch errors
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch work units status');
    } finally {
      setLoading(false);
    }
  };
  const getLinkedinAccountDisplayName = (workUnit: WorkUnit) => {
    const accountId = _.get(workUnit, 'specs.accountId');
    if (accountId && accountNames[accountId]) return accountNames[accountId];
    return workUnit.account?.email || t('unknown');
  };

  const getPersonDisplayName = (workUnit: WorkUnit) => {
    const specs = workUnit.specs || {};
    const fromSpecs = specs.contactName || specs.personName || specs.profileName || _.compact([specs.firstName, specs.lastName]).join(' ').trim();
    const fromContact = workUnit.contact?.name || workUnit.contact?.fullName || _.compact([workUnit.contact?.firstName, workUnit.contact?.lastName]).join(' ').trim();
    const name = (fromSpecs && String(fromSpecs).trim()) || (fromContact && String(fromContact).trim());
    return name || t('unknown');
  };

  useEffect(() => {
    if (open) {
      fetchWorkUnitsStatus();

      // Set up polling every 30 seconds
      const interval = setInterval(fetchWorkUnitsStatus, 30000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [open, token, funnelId]);

  const getWorkTypeColor = (workType: string) => {
    const colors: Record<string, string> = {
      send: theme.palette.success.main,
      analyze: theme.palette.secondary.main,
      search: theme.palette.sales.main,
      write: theme.palette.analysisSecondary.main,
      wait: theme.palette.greys.dark,
      judge: theme.palette.success.main,
      checkIfConnected: theme.palette.secondary.dark,
      checkIfConnectedUntil: theme.palette.secondary.dark,
      withdraw: theme.palette.error.main,
      checkInbox: theme.palette.warning.main,
      checkConnection: theme.palette.secondary.dark,
      reply: theme.palette.success.main,
      aiSearch: theme.palette.sales.dark,
    };
    return colors[workType] || theme.palette.text.secondary;
  };

  const getPriorityColor = (priority: string) => {
    return priority === 'high' ? theme.palette.error.main : theme.palette.text.secondary;
  };

  // Removed time/progress helpers per simplified UI

  const getFunctionName = (functionName: string) => {
    switch (functionName) {
      case FunnelFunctions.CHECK_IF_CONNECTED:
        return t('connectedOnLinkedIn');
      case FunnelFunctions.CHECK_IF_CONNECTED_UNTIL:
        return t('contactRequestAccepted');
      case FunnelFunctions.CHECK_IF_GENERATED_EMAIL_ADDRESS:
        return t('hasEmailAddress');
      case FunnelFunctions.CHECK_IS_POLITE:
        return t('politeSpeechQuestion', { defaultValue: t('politeSpeech') + '?' });
      case FunnelFunctions.WAIT_STEP:
        return t('waitingTime');
      case FunnelFunctions.SEND_CALL_TRANSCRIPT:
        return t('sendCallTranscript');

      default:
        // If there's no custom case, fall back to the raw function name
        return t(functionName);
    }
  };

  const handleDeleteWorkUnit = (workUnit: WorkUnit) => {
    setDeleteDialog({
      open: true,
      loading: false,
      workUnit,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      loading: false,
      workUnit: null,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.workUnit || !token) return;

    setDeleteDialog(prev => ({ ...prev, loading: true }));

    try {
      const response = await deleteWorkUnit(deleteDialog.workUnit.id);
      if (response.error) {
        setError(response.error.message || 'Failed to delete work unit');
      } else {
        // Refresh the work units status after successful deletion
        await fetchWorkUnitsStatus();
        handleCloseDeleteDialog();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete work unit');
    } finally {
      setDeleteDialog(prev => ({ ...prev, loading: false }));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth classes={{ paper: classes.paper }}>
      <DialogTitle className={classes.title}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography className={classes.titleText}>{funnelId ? t('campaignWorkUnitsStatus') : t('globalWorkUnitsStatus')}</Typography>
          <Stack direction="row" alignItems="center" gap={3}>
            <PaiButton
              text={t('refresh')}
              size="small"
              border="rounded"
              color="success"
              startIcon={<RefreshIcon />}
              action={fetchWorkUnitsStatus}
              loading={loading}
            />
            <IconButton className={classes.closeButton} onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {loading && !status && (
          <Box className={classes.loadingContainer}>
            <LinearProgress />
            <Typography className={classes.loadingText}>{t('loadingWorkUnitsStatus')}</Typography>
          </Box>
        )}

        {error && (
          <Box className={classes.errorContainer}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {status && (
          <Stack gap={4}>
            {/* System Statistics */}
            <Box>
              <Typography className={classes.sectionTitle}>{t('systemStatus')}</Typography>
              <Box className={classes.statisticsSection}>
                <Stack direction="row" gap={2} flexWrap="wrap">
                  <Box className={classes.statCard}>
                    <Stack direction="row" alignItems="center" gap={2} justifyContent="center" className={classes.statHeader}>
                      <EngineeringIcon className={classes.statIcon} />
                      <Typography className={classes.statLabel}>{t('workingUnits')}</Typography>
                    </Stack>
                    <Typography className={classes.statValue}>{status.statistics.totalProcessing}</Typography>
                  </Box>
                  <Box className={classes.statCard}>
                    <Stack direction="row" alignItems="center" gap={2} justifyContent="center" className={classes.statHeader}>
                      <PendingActionsIcon className={classes.statIcon} />
                      <Typography className={classes.statLabel}>{t('pendingUnits')}</Typography>
                    </Stack>
                    <Typography className={classes.statValue}>{status.statistics.totalPending}</Typography>
                  </Box>
                  {status.statistics.worktimeBreakdown && (
                    <Box className={classes.statCard}>
                      <Stack direction="row" alignItems="center" gap={2} justifyContent="center" className={classes.statHeader}>
                        <SpeedIcon className={classes.statIcon} />
                        <Typography className={classes.statLabel}>{t('worktime')}</Typography>
                      </Stack>
                      <Typography className={classes.text}>
                        {t('inWorktime')}: {status.statistics.worktimeBreakdown.inWorktime} · {t('outsideWorktime')}: {status.statistics.worktimeBreakdown.outOfWorktime}
                      </Typography>
                    </Box>
                  )}
                  <Box className={classes.statCard}>
                    <Stack direction="row" alignItems="center" gap={2} justifyContent="center" className={classes.statHeader}>
                      <BugReportIcon className={classes.statIcon} />
                      <Typography className={classes.statLabel}>{t('recentFailures')}</Typography>
                    </Stack>
                    <Typography
                      className={classes.statValue}
                      style={{ color: status.statistics.recentFailures > 0 ? theme.palette.error.main : 'inherit' }}
                    >
                      {status.statistics.recentFailures}
                    </Typography>
                  </Box>
                  <Box className={classes.statCard}>
                    <Stack direction="row" alignItems="center" gap={2} justifyContent="center" className={classes.statHeader}>
                      <SpeedIcon className={classes.statIcon} />
                      <Typography className={classes.statLabel}>{t('systemLoad')}</Typography>
                    </Stack>
                    <Typography className={classes.statValue}>{status.statistics.systemLoad}%</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={status.statistics.systemLoad}
                      className={classes.loadBar}
                      color={status.statistics.systemLoad > 80 ? 'error' : status.statistics.systemLoad > 60 ? 'warning' : 'success'}
                    />
                  </Box>
                </Stack>
              </Box>
            </Box>

            {/* Currently Processing */}
            <Box>
              <Typography className={classes.sectionTitle}>
                {t('currentlyProcessing')} ({status.processing.length})
              </Typography>
              <Box className={classes.processingSection}>
                {status.processing.length === 0 ? (
                  <Typography className={classes.emptyText}>{t('noWorkUnitsProcessing')}</Typography>
                ) : (
                  <Stack gap={2}>
                    {status.processing.map((item) => (
                      <ProcessingWorkUnit
                        key={item.id}
                        workUnit={item}
                        getWorkTypeColor={getWorkTypeColor}
                        getPriorityColor={getPriorityColor}
                        getFunctionName={getFunctionName}
                        getLinkedinAccountDisplayName={getLinkedinAccountDisplayName}
                        getPersonDisplayName={getPersonDisplayName}
                        onDelete={handleDeleteWorkUnit}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>

            {/* Queue */}
            <Box>
              <Typography className={classes.sectionTitle}>
                {t('upNext')} ({status.pending.length})
              </Typography>
              <Box className={classes.queueSection}>
                {status.pending.length === 0 ? (
                  <Typography className={classes.emptyText}>{t('noWorkUnitsInQueue')}</Typography>
                ) : (
                  <Stack gap={2}>
                    {status.pending.map((item) => (
                      <QueueWorkUnit
                        key={item.id}
                        workUnit={item}
                        getWorkTypeColor={getWorkTypeColor}
                        getFunctionName={getFunctionName}
                        getLinkedinAccountDisplayName={getLinkedinAccountDisplayName}
                        getPersonDisplayName={getPersonDisplayName}
                        onDelete={handleDeleteWorkUnit}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>
      {status && (
        <DialogActions className={classes.actions}>
          {/* Last Updated */}
          <Box className={classes.lastUpdated}>
            <Typography className={classes.lastUpdatedText}>
              {t('lastUpdated')}:{' '}
              {formatDistanceToNow(new Date(status.timestamp), {
                addSuffix: true,
                locale: i18n.languages[0].includes('en') ? enUS : de,
              })}
            </Typography>
          </Box>
        </DialogActions>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteWorkUnitDialog
        open={deleteDialog.open}
        loading={deleteDialog.loading}
        workUnit={deleteDialog.workUnit}
        handleClose={handleCloseDeleteDialog}
        handleConfirm={handleConfirmDelete}
      />
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  closeButton: {
    color: '#FFF',
    padding: '4px',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  errorText: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.error.main,
    marginTop: '4px',
  },
  loadingContainer: {
    padding: '40px 0',
    textAlign: 'center',
  },
  loadingText: {
    marginTop: '16px',
    color: theme.palette.text.secondary,
  },
  errorContainer: {
    padding: '40px 0',
    textAlign: 'center',
  },
  statisticsSection: {
    padding: '16px',
    background: theme.palette.backgrounds.light,
    borderRadius: '9px',
    border: `1px solid ${theme.palette.divider}`,
  },
  sectionTitle: {
    marginBottom: '16px',
    marginLeft: '4px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(16),
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&::before': {
      content: '""',
      width: '4px',
      height: '20px',
      backgroundColor: theme.palette.primary.main,
      borderRadius: '2px',
    },
  },
  statCard: {
    flex: 1,
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '8px',
    minWidth: '160px',
    textAlign: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  statHeader: {
    marginBottom: '12px',
    alignItems: 'center',
  },
  statIcon: {
    fontSize: theme.typography.pxToRem(28),
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
    fontWeight: 500,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  loadBar: {
    marginTop: '12px',
    borderRadius: '6px',
    height: '8px',
  },
  processingSection: {
    padding: '16px',
    background: lighten(theme.palette.success.main, 0.8),
    borderRadius: '9px',
    border: `1px solid ${theme.palette.success.main}`,
  },
  queueSection: {
    padding: '16px',
    background: lighten(theme.palette.warning.main, 0.8),
    borderRadius: '9px',
    border: `1px solid ${theme.palette.warning.main}`,
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '16px',
  },
  workItem: {
    position: 'relative',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  workItemHeader: {
    marginBottom: '12px',
    justifyContent: 'flex-start',
  },
  workTypeChip: {
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(12),
  },
  priorityChip: {
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(12),
  },
  workItemStatus: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '4px 8px',
    backdropFilter: 'blur(4px)',
  },
  statusIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    animation: 'pulse 2s infinite',
  },
  statusText: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 600,
    color: theme.palette.success.main,
    textTransform: 'uppercase',
  },
  workItemInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '8px',
    marginBottom: '12px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  infoLabel: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  infoValue: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  workItemTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: '12px',
  },
  progressSection: {
    marginTop: '8px',
  },
  progressHeader: {
    marginBottom: '4px',
  },
  progressLabel: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
  },
  queueItem: {
    position: 'relative',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  queueItemHeader: {
    marginBottom: '12px',
    justifyContent: 'flex-start',
  },
  queueWorkTypeChip: {
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(12),
  },
  queueItemStatus: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '4px 8px',
    backdropFilter: 'blur(4px)',
  },
  queueItemInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '8px',
  },
  queueStatusIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.main,
  },
  queueStatusText: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 600,
    color: theme.palette.warning.main,
    textTransform: 'uppercase',
  },
  queueItemTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: '12px',
  },
  progressBar: {
    flex: 1,
    borderRadius: '6px',
    height: '8px',
  },
  progressText: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    minWidth: '32px',
  },
  queuePosition: {
    backgroundColor: theme.palette.greys.lighter,
    color: 'theme.palette.text.primary',
    padding: '4px 8px',
    borderRadius: '50%',
    fontSize: theme.typography.pxToRem(12),
    minWidth: '24px',
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px 0 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  lastUpdated: {
    textAlign: 'center',
    padding: '16px 0',
  },
  lastUpdatedText: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  deleteButton: {
    color: theme.palette.error.main,
    padding: '2px',
    marginLeft: '4px',
    '&:hover': {
      backgroundColor: 'rgba(211, 47, 47, 0.08)',
    },
  },
}));

export default WorkUnitsStatusModal;
